import axios, { AxiosError } from 'axios';

/**
 * Requirement Dependency Service
 *
 * Handles all requirement dependency-related API requests to the backend.
 * Manages workflow-based dependencies between requirements for Gantt chart visualization.
 */

// API Base URL from environment variable or default to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * Requirement Dependency interfaces
 */
export interface RequirementDependency {
  id: string;
  requirement_id: string;
  depends_on_requirement_id: string;
  dependency_type: 'finish-to-start' | 'start-to-start' | 'finish-to-finish' | 'start-to-finish';
  lag_days: number;
  created_by: string;
  created_at: string;
  is_active: boolean;
}

export interface CreateDependencyInput {
  depends_on_requirement_id: string;
  dependency_type?: 'finish-to-start' | 'start-to-start' | 'finish-to-finish' | 'start-to-finish';
  lag_days?: number;
}

export interface UpdateDependencyInput {
  dependency_type?: 'finish-to-start' | 'start-to-start' | 'finish-to-finish' | 'start-to-finish';
  lag_days?: number;
}

export interface APIError {
  error: string;
  details?: string;
}

/**
 * Handle API errors
 */
function handleAPIError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<APIError>;
    const errorMessage = axiosError.response?.data?.error || axiosError.message;
    throw new Error(errorMessage);
  }
  throw error;
}

/**
 * Create a new dependency for a requirement
 */
export async function createDependency(
  projectId: string,
  requirementId: string,
  data: CreateDependencyInput,
  token: string
): Promise<RequirementDependency> {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}/dependencies`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get all dependencies for a specific requirement
 */
export async function getDependencies(
  projectId: string,
  requirementId: string,
  token: string
): Promise<RequirementDependency[]> {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}/dependencies`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get all dependencies for a project
 */
export async function getProjectDependencies(
  projectId: string,
  token: string
): Promise<RequirementDependency[]> {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/projects/${projectId}/dependencies`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Update a requirement dependency
 */
export async function updateDependency(
  projectId: string,
  requirementId: string,
  dependencyId: string,
  data: UpdateDependencyInput,
  token: string
): Promise<RequirementDependency> {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}/dependencies/${dependencyId}`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Delete a requirement dependency (soft delete)
 */
export async function deleteDependency(
  projectId: string,
  requirementId: string,
  dependencyId: string,
  token: string
): Promise<void> {
  try {
    await axios.delete(
      `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}/dependencies/${dependencyId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
  } catch (error) {
    handleAPIError(error);
  }
}

export default {
  createDependency,
  getDependencies,
  getProjectDependencies,
  updateDependency,
  deleteDependency
};
